import {
  Currency,
  CurrencyAmount,
  FACTORIES_MAP,
  FactoryVersion,
  JSBI,
  Pair,
  PairVariant,
  Percent,
  Price,
  RouterVersion,
  Token,
  BIG_0,
} from '@nominex/nomiswap-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import { PairState, usePair } from 'hooks/usePairs'
import useTotalSupply from 'hooks/useTotalSupply'
import { AppState, useAppDispatch } from 'state'
import { useCurrencyBalances } from 'state/wallet/hooks'
import { getRouterContract } from 'utils/contractHelpers'
import { getCurrencyPrice } from 'utils/currency'
import { getBalanceAmount, getDecimalAmount } from 'utils/formatBalance'
import tryParseAmount from 'utils/tryParseAmount'
import { formatNumber } from 'utils/formatters'
import { OraclePool } from 'hooks/useOraclePools'
import { Field, typeInput } from './actions'
import { State } from '../types'

const PRICE_ONE = 1e18

function isNoLiquidity(totalSupply: JSBI, reserve0: JSBI, reserve1: JSBI): boolean {
  return Boolean(JSBI.equal(totalSupply, BIG_0)) || Boolean(JSBI.equal(reserve0, BIG_0) && JSBI.equal(reserve1, BIG_0))
}

export function useMintState(): AppState['mint'] {
  return useSelector<AppState, AppState['mint']>((state) => state.mint)
}

export function useMintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
} {
  const dispatch = useAppDispatch()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity],
  )
  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity],
  )

  return {
    onFieldAInput,
    onFieldBInput,
  }
}

export function useDerivedMintInfo(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  factoryVersion?: FactoryVersion,
): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  pair?: Pair | null
  pairState: PairState
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  price?: Price<Currency, Currency>
  noLiquidity?: boolean
} {
  const { account } = useWagmi()

  const { independentField, typedValue, otherTypedValue } = useMintState()

  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  // tokens
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA ?? undefined,
      [Field.CURRENCY_B]: currencyB ?? undefined,
    }),
    [currencyA, currencyB],
  )

  // pair
  const [pairState, pair] = usePair(currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B], factoryVersion)

  const totalSupply = useTotalSupply(pair?.liquidityToken)

  const noLiquidity: boolean =
    pairState === PairState.NOT_EXISTS ||
    (totalSupply ? isNoLiquidity(totalSupply.quotient, pair.reserve0.quotient, pair.reserve1.quotient) : false)

  // balances
  const balances = useCurrencyBalances(account ?? undefined, [
    currencies[Field.CURRENCY_A],
    currencies[Field.CURRENCY_B],
  ])
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1],
  }

  // amounts
  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseAmount(
    typedValue,
    currencies[independentField],
  )
  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (noLiquidity) {
      if (otherTypedValue && currencies[dependentField]) {
        return tryParseAmount(otherTypedValue, currencies[dependentField])
      }
      return undefined
    }
    if (independentAmount) {
      // we wrap the currencies just to get the price in terms of the other token
      const wrappedIndependentAmount = independentAmount?.wrapped
      const [tokenA, tokenB] = [currencyA?.wrapped, currencyB?.wrapped]

      if (tokenA && tokenB && wrappedIndependentAmount && pair) {
        const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
        let dependentTokenAmount

        if (pair.variant === PairVariant.STABLE_SWAP) {
          dependentTokenAmount = pair.quote(wrappedIndependentAmount)
        } else {
          dependentTokenAmount =
            dependentField === Field.CURRENCY_B
              ? pair.priceOf(tokenA).quote(wrappedIndependentAmount)
              : pair.priceOf(tokenB).quote(wrappedIndependentAmount)
        }

        return dependentCurrency?.isNative
          ? CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount.quotient)
          : dependentTokenAmount
      }
      return undefined
    }
    return undefined
  }, [noLiquidity, otherTypedValue, currencies, dependentField, independentAmount, currencyA, currencyB, pair])

  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(
    () => ({
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }),
    [dependentAmount, independentAmount, independentField],
  )

  const price = useMemo(() => {
    if (noLiquidity) {
      const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
      if (currencyAAmount && currencyBAmount) {
        return new Price(
          currencyAAmount.currency,
          currencyBAmount.currency,
          currencyAAmount.quotient,
          currencyBAmount.quotient,
        )
      }
      return undefined
    }
    const wrappedCurrencyA = currencyA?.wrapped
    return pair && wrappedCurrencyA ? pair.priceOf(wrappedCurrencyA) : undefined
  }, [currencyA, noLiquidity, pair, parsedAmounts])

  return {
    dependentField,
    currencies,
    pair,
    pairState,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
  }
}

export function useDerivedEPCSMintInfo(
  pool: OraclePool,
  baseCurrency: Currency | undefined,
  quoteCurrency: Currency | undefined,
  fieldState: {
    independentField: Field
    dependentField: Field
  },
  typedValue: string,
  otherTypedValue: string,
): {
  currencies: { [field in Field]?: Currency }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  formattedAmounts: { [field in Field]?: string }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  typedValues: { [field in Field]?: string }
  noLiquidity?: boolean
  liquidityMinted?: CurrencyAmount<Token>
  validationError?: string
} {
  const { MINIMUM_LIQUIDITY, liquidityToken, baseReserve, quoteReserve, price, totalSupply } = pool
  const { independentField, dependentField } = fieldState

  const { account } = useWagmi()
  const { t } = useTranslation()

  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: baseCurrency ?? undefined,
      [Field.CURRENCY_B]: quoteCurrency ?? undefined,
    }),
    [baseCurrency, quoteCurrency],
  )

  const balances = useCurrencyBalances(account ?? undefined, [
    currencies[Field.CURRENCY_A],
    currencies[Field.CURRENCY_B],
  ])

  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1],
  }

  const noLiquidity: boolean = isNoLiquidity(totalSupply.quotient, JSBI.BigInt(baseReserve), JSBI.BigInt(quoteReserve))

  let validationError: string | undefined

  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseAmount(
    independentField === Field.CURRENCY_A ? typedValue : otherTypedValue,
    currencies[independentField],
  )

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    const dependentCurrency = currencies[dependentField]

    if (!independentAmount) {
      return undefined
    }

    if (noLiquidity) {
      if (otherTypedValue && dependentCurrency) {
        return tryParseAmount(otherTypedValue, dependentCurrency)
      }
      return undefined
    }

    const dependentTokenAmount =
      dependentField === Field.CURRENCY_B
        ? JSBI.divide(
            JSBI.multiply(JSBI.BigInt(independentAmount.quotient), JSBI.BigInt(quoteReserve)),
            JSBI.BigInt(baseReserve),
          )
        : JSBI.divide(
            JSBI.multiply(JSBI.BigInt(independentAmount.quotient), JSBI.BigInt(baseReserve)),
            JSBI.BigInt(quoteReserve),
          )

    return CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount)
  }, [noLiquidity, otherTypedValue, currencies, dependentField, independentAmount, baseReserve, quoteReserve])

  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(
    () => ({
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }),
    [dependentAmount, independentAmount, independentField],
  )

  const formattedAmounts = {
    [independentField]: formatNumber(parsedAmounts[independentField]?.toExact(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: currencies[independentField].decimals,
    }),
    [dependentField]: formatNumber(parsedAmounts[dependentField]?.toExact(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: currencies[dependentField].decimals,
    }),
  }

  const typedValues = useMemo(
    () => ({
      [independentField]: typedValue,
      [dependentField]: noLiquidity
        ? otherTypedValue
        : parsedAmounts[dependentField]?.toSignificant(currencies[dependentField].decimals),
    }),
    [typedValue, otherTypedValue, parsedAmounts, dependentField, independentField, noLiquidity, currencies],
  )

  const liquidityMinted = useMemo(() => {
    const baseAmount = parsedAmounts[Field.CURRENCY_A]
    const quoteAmount = parsedAmounts[Field.CURRENCY_B]

    if (!baseAmount || !quoteAmount || baseAmount.equalTo(BIG_0) || quoteAmount.equalTo(BIG_0)) {
      return null
    }

    let liquidity = BIG_0

    try {
      if (noLiquidity) {
        liquidity = JSBI.divide(
          JSBI.multiply(JSBI.BigInt(baseAmount.quotient), JSBI.BigInt(price)),
          JSBI.subtract(
            JSBI.add(JSBI.BigInt(PRICE_ONE), JSBI.BigInt(quoteAmount.quotient)),
            JSBI.BigInt(MINIMUM_LIQUIDITY),
          ),
        )
      } else {
        const amount0 = JSBI.divide(
          JSBI.multiply(JSBI.BigInt(baseAmount.quotient), totalSupply.quotient),
          JSBI.BigInt(baseReserve),
        )
        const amount1 = JSBI.divide(
          JSBI.multiply(JSBI.BigInt(quoteAmount.quotient), totalSupply.quotient),
          JSBI.BigInt(quoteReserve),
        )

        liquidity = JSBI.lessThanOrEqual(amount0, amount1) ? amount0 : amount1
      }

      return CurrencyAmount.fromRawAmount(liquidityToken, liquidity)
    } catch (error) {
      console.error(error)
      return null
    }
  }, [parsedAmounts, MINIMUM_LIQUIDITY, baseReserve, liquidityToken, noLiquidity, price, quoteReserve, totalSupply])

  // eslint-disable-next-line default-case
  switch (true) {
    case !account:
      validationError = t('Connect Wallet')
      break
    case !parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]:
      validationError = t('Enter an amount')
      break
    case currencyBalances?.[Field.CURRENCY_A]?.lessThan(parsedAmounts[Field.CURRENCY_A]):
      validationError = t('Insufficient %symbol% balance', { symbol: currencies[Field.CURRENCY_A]?.symbol })
      break
    case currencyBalances?.[Field.CURRENCY_B]?.lessThan(parsedAmounts[Field.CURRENCY_B]):
      validationError = t('Insufficient %symbol% balance', { symbol: currencies[Field.CURRENCY_B]?.symbol })
      break
  }

  return {
    currencies,
    currencyBalances,
    formattedAmounts,
    parsedAmounts,
    typedValues,
    noLiquidity,
    liquidityMinted,
    validationError,
  }
}

export function useCustomProportionToAddLiquidity(
  currencies: { [field in Field]?: Currency },
  formattedAmounts: { [field in Field]?: string },
  pair: Pair | null,
): {
  amountAToSwap: string
  amountBToSwap: string
  amountAToAdd: string
  amountBToAdd: string
  amountAReceived: string
  amountBReceived: string
  amountAInUsdt: number
  amountBInUsdt: number
  poolShareA: number
  poolShareB: number
  totalAmountInUsdt: number
  liquidityMinted?: CurrencyAmount<Token>
  poolTokenPercentage?: Percent
} {
  const { data: farms } = useSelector((state: State) => state.farms)
  const { chainId } = useWagmi()

  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]

  const [amountAToSwap, setAmountAToSwap] = useState('0')
  const [amountBToSwap, setAmountBToSwap] = useState('0')
  const [amountAReceived, setAmountAReceived] = useState('0')
  const [amountBReceived, setAmountBReceived] = useState('0')

  const decimalAmountA = getDecimalAmount(Number(formattedAmounts[Field.CURRENCY_A]), currencyA?.decimals)
  const decimalAmountB = getDecimalAmount(Number(formattedAmounts[Field.CURRENCY_B]), currencyB?.decimals)

  const [balance0, balance1] = pair?.token0.equals(currencyA?.wrapped)
    ? [decimalAmountA.toString(), decimalAmountB.toString()]
    : [decimalAmountB.toString(), decimalAmountA.toString()]

  useEffect(() => {
    if (pair) {
      const router = getRouterContract(RouterVersion.V3, chainId)

      router
        .balanceLiquidity(
          FACTORIES_MAP[chainId][pair.factoryVersion].address,
          pair.token0.address,
          pair.token1.address,
          balance0,
          balance1,
        )
        .then(([amount0ToSwap, amount1ToSwap, amount0Received, amount1Received]) => {
          if (pair.token0.equals(currencyA?.wrapped)) {
            setAmountAToSwap(amount0ToSwap.toString())
            setAmountBToSwap(amount1ToSwap.toString())
            setAmountAReceived(amount0Received.toString())
            setAmountBReceived(amount1Received.toString())
          } else {
            setAmountBToSwap(amount0ToSwap.toString())
            setAmountAToSwap(amount1ToSwap.toString())
            setAmountBReceived(amount0Received.toString())
            setAmountAReceived(amount1Received.toString())
          }
        })
    }
  }, [chainId, balance0, balance1, pair, currencyA?.wrapped])

  const decimalAmountAToAdd = decimalAmountA.minus(amountAToSwap).plus(amountAReceived)
  const decimalAmountBToAdd = decimalAmountB.minus(amountBToSwap).plus(amountBReceived)

  const amountAToAdd = decimalAmountAToAdd.toString()
  const amountBToAdd = decimalAmountBToAdd.toString()

  const amountAInUsdt = getBalanceAmount(
    decimalAmountAToAdd.times(getCurrencyPrice(currencyA, farms)),
    currencyA?.decimals,
  ).toNumber()
  const amountBInUsdt = getBalanceAmount(
    decimalAmountBToAdd.times(getCurrencyPrice(currencyB, farms)),
    currencyB?.decimals,
  ).toNumber()
  const totalAmountInUsdt = amountAInUsdt + amountBInUsdt

  const poolShareA = amountAInUsdt / totalAmountInUsdt || 0.5
  const poolShareB = amountBInUsdt / totalAmountInUsdt || 0.5

  const totalSupply = useTotalSupply(pair?.liquidityToken)
  // liquidity minted
  const liquidityMinted = useMemo(() => {
    const tokenAmountA = tryParseAmount(
      getBalanceAmount(decimalAmountAToAdd, currencyA?.decimals).toString(),
      currencyA,
    ) as CurrencyAmount<Token>
    const tokenAmountB = tryParseAmount(
      getBalanceAmount(decimalAmountBToAdd, currencyB?.decimals).toString(),
      currencyB,
    ) as CurrencyAmount<Token>

    try {
      return pair.getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB)
    } catch {
      return undefined
    }
  }, [currencyA, currencyB, decimalAmountAToAdd, decimalAmountBToAdd, pair, totalSupply])

  const poolTokenPercentage = useMemo(() => {
    if (liquidityMinted && totalSupply) {
      return new Percent(liquidityMinted.quotient, totalSupply.add(liquidityMinted).quotient)
    }
    return undefined
  }, [liquidityMinted, totalSupply])

  return {
    amountAToSwap,
    amountBToSwap,
    amountAToAdd,
    amountBToAdd,
    amountAReceived,
    amountBReceived,
    amountAInUsdt,
    amountBInUsdt,
    liquidityMinted,
    poolTokenPercentage,
    poolShareA,
    poolShareB,
    totalAmountInUsdt,
  }
}
